import { Suspense, lazy, useMemo, useRef, useState } from 'react';
import type { AvatarAnimationClip } from '@/services/avatarApi';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';
import { setLastPickedAnimation, getLastPickedAnimation } from '@/lib/lastPickedAnimation';

const AnimatedModelViewer = lazy(() => import('@/components/hub/AnimatedModelViewer'));

function humanize(id: string): string {
  // 'meshy-dance-22' → 'Dance 22'; 'idle' → 'Idle'
  return id
    .replace(/^(meshy|tripo)-/, '')
    .split(/[-_]/)
    .filter(Boolean)
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

interface Props {
  avatarId: string;
  /** ULID-style pack id for per-pack last-picked persistence (Phase 3). */
  packId?: string;
  glbUrl: string;
  poster?: string;
  animations: AvatarAnimationClip[];
  /** Optional label override per preset id (caller passes via library catalog lookup). */
  presetLabels?: Record<string, string>;
}

export function AnimationPicker({ avatarId, packId, glbUrl, poster, animations, presetLabels }: Props) {
  const haptics = useHaptics();
  const ready = useMemo(
    () => animations.filter(a => a.status === 'ready' && (a.glbUrl || true)),
    [animations],
  );
  const storeKey = packId ? `${avatarId}:${packId}` : avatarId;
  const initial = (() => {
    const stored = getLastPickedAnimation(storeKey);
    if (stored && ready.find(r => r.preset === stored)) return stored;
    const idle = ready.find(r => r.preset === 'idle' || r.preset === 'meshy-walking');
    return (idle?.preset ?? ready[0]?.preset ?? '') as string;
  })();
  const [selected, setSelected] = useState<string>(initial);
  const viewerRef = useRef<HTMLElement>(null);

  const onPick = (preset: string) => {
    haptics.impact('light');
    setSelected(preset);
    setLastPickedAnimation(storeKey, preset);
  };

  const labelFor = (id: string) => presetLabels?.[id] ?? humanize(id);

  return (
    <div className="flex flex-col gap-3">
      <Suspense fallback={
        <img src={poster} alt="" className="w-full rounded-2xl object-cover aspect-square" />
      }>
        <AnimatedModelViewer
          ref={viewerRef}
          src={glbUrl}
          poster={poster}
          animationName={selected}
          autoplay
          cameraOrbit="90deg 90deg 105%"
          cameraControls
          className="aspect-square w-full rounded-2xl bg-bg-tertiary"
        />
      </Suspense>
      <div className="flex gap-2 overflow-x-auto px-1 pb-1 -mx-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {ready.map(clip => (
          <button
            key={clip.preset}
            type="button"
            onClick={() => onPick(clip.preset)}
            className={cn(
              'shrink-0 rounded-full px-4 py-2 text-xs font-semibold transition-colors',
              selected === clip.preset ? 'bg-brand text-bg' : 'bg-bg-secondary text-fg',
            )}
          >
            {labelFor(clip.preset)}
          </button>
        ))}
      </div>
    </div>
  );
}
